import { useMemo, useState } from "react";

import {
  classifyResponseFilteringWriteFailure,
  useReplaceResponseFilteringMutation,
  useResponseFilteringQuery,
  type ProjectResponseFiltering,
} from "@/api/responseFiltering";
import { useCapability } from "@/auth/SessionContext";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";

/**
 * Response filtering — the Project Rules advanced control for the bounded
 * Output Guard profile (#860).
 *
 * ## WHERE IT SITS, AND WHY IT IS ONE ROW
 *
 * It renders in `ProjectSectionPane`'s `controls` slot on the Rules section —
 * the stable insertion point #909 declared for exactly this, so nothing about
 * `SectionSurface`, `SectionAdoptionPanel` or the Rules body is restructured to
 * make room for it. The [Lovable Rules
 * screen](https://noproductcontent.lovable.app/projects/mod/config/rules) is
 * compact adopted-rule rows under a top-right adoption action, and Response
 * filtering is an explicit ADDITION to it, so it takes that screen's own shape:
 * one compact bordered row, name on the left, state on the right, its editor
 * behind an action rather than expanded in front of a clinical author who came
 * to the page to adopt a language rule. #910 owns the rows below; this control
 * touches none of them.
 *
 * ## THE SENTENCE THIS SURFACE MUST GET RIGHT
 *
 * These signals decide WHEN a reply is checked by the guard classifier. They
 * are not a verdict, and removing one grants no permission: the classifier
 * still judges every reply it is given, and a redirect is its decision. The
 * copy says that in the dialog, where the decision is actually made, because
 * the plausible misreading of a control called "Response filtering" is that its
 * list is what gets blocked.
 *
 * ## WHAT IT WILL NOT OFFER
 *
 * There is no control that empties the list, and none that reaches any other
 * output-guard field: the protected keys are named as read-only text from the
 * server's own `protectedFields`. Where the server says authoring is
 * unavailable — the pinned engine locks the document, or the base list could
 * not be read — Save is not rendered at all and the reason is stated, ADR
 * 0001's no-dead-controls rule. Reset stays offered whenever an override
 * exists, because removing a decision is the recovery out of a degraded state.
 */
export function ResponseFilteringControl({ projectId }: { projectId: string }) {
  const query = useResponseFilteringQuery(projectId);
  const write = useCapability("draft.write");
  const [open, setOpen] = useState(false);

  const state = query.data ?? null;

  if (query.isPending || state === null) {
    // Deliberately quiet, not a skeleton block: this control sits ABOVE the
    // section's rows, and a loading card there pushes the clinical body down
    // the page on every navigation. A failed read renders the same line — the
    // Rules rows are a different read and stay usable.
    return (
      <p
        className="rounded-md border border-border bg-card px-5 py-4 text-sm text-muted-foreground"
        data-slot="response-filtering"
      >
        {query.isPending
          ? "Reading this project's response filtering…"
          : "This project's response filtering could not be read. The rules below are unaffected."}
      </p>
    );
  }

  const effectiveCount = state.effectiveSignals?.length ?? null;
  const removedCount = state.removedSignals?.length ?? 0;
  const addedCount = state.addedSignals?.length ?? 0;
  const overridden = state.state === "overridden";

  return (
    <div
      data-slot="response-filtering"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-card px-5 py-4"
    >
      <div className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            Response filtering
          </span>
          <Badge tone={overridden ? "accent" : "outline"}>
            {overridden ? "Project list" : "Inherited"}
          </Badge>
        </span>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {describe({ effectiveCount, removedCount, addedCount, overridden })}
        </p>
      </div>

      <div className="relative z-10 flex shrink-0 items-center gap-1">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          {/* One control whatever the account may do: a reader opens the same
              dialog and reads the effective list there. Naming it "Configure"
              for a reader would promise a write they do not hold. */}
          {write.allowed ? "Configure" : "View"}
        </Button>
      </div>

      {open ? (
        <ResponseFilteringDialog
          projectId={projectId}
          state={state}
          canWrite={write.allowed}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function describe({
  effectiveCount,
  removedCount,
  addedCount,
  overridden,
}: {
  effectiveCount: number | null;
  removedCount: number;
  addedCount: number;
  overridden: boolean;
}): string {
  if (effectiveCount === null) {
    return "The base pack's signal list could not be read, so this project's effective list is unknown.";
  }
  const counts = `${effectiveCount} ${effectiveCount === 1 ? "signal" : "signals"}`;
  if (!overridden) {
    return `${counts}, inherited from the base pack. Signals decide when a reply is checked by the guard classifier.`;
  }
  const deltas = [
    removedCount > 0 ? `${removedCount} removed` : null,
    addedCount > 0 ? `${addedCount} added` : null,
  ].filter((part): part is string => part !== null);
  return deltas.length > 0
    ? `${counts} authored for this project — ${deltas.join(", ")} against the base list.`
    : `${counts} authored for this project, identical to the base list.`;
}

function ResponseFilteringDialog({
  projectId,
  state,
  canWrite,
  onClose,
}: {
  projectId: string;
  state: ProjectResponseFiltering;
  canWrite: boolean;
  onClose: () => void;
}) {
  const mutation = useReplaceResponseFilteringMutation(projectId);
  // THE TOKEN THIS DIALOG OPENED ON, captured once and never re-read.
  //
  // `state` is the live query, and a stale refusal REFETCHES it — so reading
  // the token off the prop at submit time handed the loser the winner's fresh
  // token while keeping the loser's local `draft`, and a second Save silently
  // overwrote the concurrent change. That is the lost update the CAS exists to
  // refuse, reintroduced above it. Pinning the token makes a second Save fail
  // the same way the first did, until the clinician reopens on the new list.
  const [openedAt] = useState(state.filteringVersion);
  // Memoized because the `??` fallback mints a NEW empty array each render,
  // which would make every dependent `useMemo` below recompute on every render
  // — the lint rule is describing a real defect, not a style preference.
  const baseline = useMemo(
    () => state.inheritedSignals ?? [],
    [state.inheritedSignals],
  );
  // The server names the keys it will never write; the browser does not
  // hard-code the engine's vocabulary. `response_model_exclude_none` can omit a
  // defaulted list, so it is read defensively rather than assumed present.
  const protectedFields = state.protectedFields ?? [];
  const [draft, setDraft] = useState<string[]>(
    () => state.effectiveSignals ?? [],
  );
  const [added, setAdded] = useState("");

  const kept = useMemo(() => new Set(draft.map((s) => s.toLowerCase())), [draft]);
  const dropped = useMemo(
    () => baseline.filter((signal) => !kept.has(signal.toLowerCase())),
    [baseline, kept],
  );
  const extra = useMemo(() => {
    const inherited = new Set(baseline.map((s) => s.toLowerCase()));
    return draft.filter((signal) => !inherited.has(signal.toLowerCase()));
  }, [baseline, draft]);

  const dirty =
    draft.length !== (state.effectiveSignals?.length ?? 0) ||
    draft.some((signal, index) => signal !== state.effectiveSignals?.[index]);
  const busy = mutation.isPending;
  // The one refusal this surface must never produce: a Save that empties the
  // list. The server refuses it and the engine would fail closed on it, so the
  // control is withheld rather than offered and then rejected (ADR 0001).
  const failure = mutation.isError
    ? classifyResponseFilteringWriteFailure(mutation.error)
    : null;
  // A stale refusal is terminal FOR THIS DIALOG. The list on screen was edited
  // against a version that no longer exists, so every further Save from it
  // would either fail identically or — if the token were refreshed — discard
  // somebody else's decision. The write controls go, the notice explains, and
  // Close is the way out.
  const stale = failure?.kind === "stale";
  const savable =
    canWrite && state.authoringAvailable && draft.length > 0 && dirty && !stale;

  // `mutate`, not `mutateAsync`: a refusal is DISPLAYED here (the notice above
  // reads `mutation.error`), so awaiting a promise that rejects would leave an
  // unhandled rejection behind every refusal — and the dialog must stay open on
  // one, which is exactly what an `onSuccess`-only close expresses.
  function submit(next: "inherited" | "overridden") {
    mutation.mutate(
      next === "inherited"
        ? { expectedFilteringVersion: openedAt, state: "inherited" }
        : {
            expectedFilteringVersion: openedAt,
            state: "overridden",
            signals: draft,
          },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent
        busy={busy}
        className="max-w-2xl"
        title="Response filtering"
        description="The signals that decide when this project's replies are checked by the guard classifier. A signal is a reason to CHECK a reply, never a verdict on it: the classifier still judges every reply it is given, and removing a signal permits nothing."
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={onClose}>
              Close
            </Button>
            {canWrite && state.state === "overridden" && !stale ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => submit("inherited")}
              >
                Reset to inherited
              </Button>
            ) : null}
            {canWrite && !stale ? (
              <Button
                disabled={!savable || busy}
                onClick={() => submit("overridden")}
              >
                Save signal list
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="space-y-4 text-sm">
          {failure ? (
            <Notice tone="destructive" role="alert">
              {refusalSentence(failure)}
            </Notice>
          ) : null}

          {!state.authoringAvailable && canWrite ? (
            <Notice tone="warning">{unavailableSentence(state)}</Notice>
          ) : null}

          <p className="text-muted-foreground">
            Inherited from the base pack:{" "}
            <strong className="text-foreground">{baseline.length}</strong>{" "}
            signals. This project runs{" "}
            <strong className="text-foreground">{draft.length}</strong>.{" "}
            {protectedFields.length > 0 ? (
              <>
                Everything else in the guard configuration stays inherited and
                cannot be edited here, including{" "}
                <code className="rounded bg-secondary px-1">
                  {protectedFields.join(", ")}
                </code>{" "}
                — the checks that run on the patient&rsquo;s own messages.
              </>
            ) : null}
          </p>

          <SignalList
            heading="Signals this project runs"
            emptyLabel="No signals. The guard cannot run with an empty list, so this cannot be saved."
            signals={draft}
            actionLabel="Remove"
            disabled={!canWrite || !state.authoringAvailable || busy}
            onAct={(signal) =>
              setDraft((current) => current.filter((item) => item !== signal))
            }
          />

          {dropped.length > 0 ? (
            <SignalList
              heading={`Removed from the inherited list (${dropped.length})`}
              signals={dropped}
              actionLabel="Restore"
              muted
              disabled={!canWrite || !state.authoringAvailable || busy}
              onAct={(signal) => setDraft((current) => [...current, signal])}
            />
          ) : null}

          {extra.length > 0 ? (
            <p className="text-muted-foreground">
              {extra.length} {extra.length === 1 ? "signal is" : "signals are"}{" "}
              not in the base list. Added signals gate MORE replies for
              checking, never fewer.
            </p>
          ) : null}

          {canWrite && state.authoringAvailable ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const value = added.trim();
                if (
                  value.length === 0 ||
                  draft.some((s) => s.toLowerCase() === value.toLowerCase())
                ) {
                  return;
                }
                setDraft((current) => [...current, value]);
                setAdded("");
              }}
            >
              <label className="sr-only" htmlFor="response-filtering-add">
                Add a signal
              </label>
              <Input
                id="response-filtering-add"
                className="max-w-xs"
                value={added}
                disabled={busy}
                placeholder="Add a signal"
                onChange={(event) => setAdded(event.target.value)}
              />
              <Button
                type="submit"
                variant="outline"
                size="sm"
                disabled={busy || added.trim().length === 0}
              >
                Add
              </Button>
            </form>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SignalList({
  heading,
  signals,
  actionLabel,
  onAct,
  disabled,
  muted = false,
  emptyLabel,
}: {
  heading: string;
  signals: readonly string[];
  actionLabel: string;
  onAct: (signal: string) => void;
  disabled: boolean;
  muted?: boolean;
  emptyLabel?: string;
}) {
  return (
    <section aria-label={heading} className="space-y-1.5">
      <h3 className="text-sm font-semibold text-foreground">{heading}</h3>
      {signals.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      ) : (
        <ul className="flex max-h-64 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-border p-2">
          {signals.map((signal) => (
            <li key={signal}>
              <button
                type="button"
                disabled={disabled}
                // NAMED with the signal, so a control listing does not hold
                // dozens of buttons all called "Remove" — the same rule the
                // section panes' ellipsis menus follow.
                aria-label={`${actionLabel} ${signal}`}
                onClick={() => onAct(signal)}
                className={`rounded border px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-60 ${
                  muted
                    ? "border-dashed border-border text-muted-foreground line-through hover:no-underline"
                    : "border-border text-foreground hover:bg-secondary"
                }`}
              >
                {signal}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Why authoring is unavailable, from the SERVER's own three-state answer.
 *
 * The server states `guard_floor_locked` and `base_unavailable` and states
 * NEITHER when the baseline is readable but the engine could not be reached —
 * a deliberate distinction, because the dependency a clinician would chase is
 * different in each case. Collapsing the absent case onto "the base pack could
 * not be read" sent them after the wrong one.
 */
function unavailableSentence(state: ProjectResponseFiltering): string {
  if (state.authoringRefusal === "guard_floor_locked") {
    const catalog = state.floor
      ? ` (guard-floor catalog ${state.floor.catalogRevision})`
      : "";
    const reset =
      state.state === "overridden"
        ? "; resetting this project to the inherited list is still available."
        : ".";
    return `The pinned clinical engine locks the output-guard document${catalog}, so no project may replace its signal list on this deployment. The list below is read-only${reset}`;
  }
  if (state.authoringRefusal === "base_unavailable") {
    return "The base pack's signal list could not be read from this deployment, so an override cannot be offered against an unknown baseline.";
  }
  return "The clinical engine that validates a signal list is not reachable from this deployment, so an override cannot be checked against its guard policy. The list below is read-only until it is available; resetting to the inherited list does not need it.";
}

function refusalSentence(
  failure: ReturnType<typeof classifyResponseFilteringWriteFailure>,
): string {
  switch (failure.kind) {
    case "stale":
      return "This project's response filtering changed while this dialog was open, so nothing was saved. Close and reopen it to see the current list before deciding again.";
    case "floor_locked":
      return "The pinned clinical engine locks the output-guard document, so no project may replace its signal list on this deployment. Nothing was changed.";
    case "upstream":
      return "The clinical engine that validates this list is not reachable, so the change was not saved. An override cannot be stored without the engine's own guard policy. Try again once it is available.";
    case "denied":
      return "This account may not change this project's response filtering. Nothing was changed.";
    case "invalid":
      return failure.message;
    case "ambiguous":
      return `The server did not answer this save${failure.requestId ? ` (request ${failure.requestId})` : ""}. Reopen this control to see whether it was applied before deciding again.`;
    default:
      return "The change was refused and nothing was saved.";
  }
}
